/**
 * Demo launch accounts (ADR 0071, ADR 0078): each agent's default sign-in, one Claude Code account
 * of each kind, and a few Codex and Gemini CLI accounts, on the demo machines. No real keys or folders.
 */
import { DEFAULT_LAUNCH_ACCOUNT_NAME, LAUNCH_ACCOUNT_AGENTS, defaultLaunchAccountId } from '@orgus/protocol';
import type { LaunchAccount, LaunchAccountDefaults, LaunchAccountsState, LaunchFolderStatus, Machine } from '@orgus/protocol';

const HOUR = 3_600_000;

function home(m: Machine): string {
  return m.os === 'windows' ? `C:\\Users\\dev` : m.os === 'macos' ? '/Users/dev' : '/home/dev';
}

function join(m: Machine, ...parts: string[]): string {
  return m.os === 'windows' ? [home(m), ...parts].join('\\') : [home(m), ...parts].join('/');
}

export function mockLaunchAccounts(machines: Machine[], projectIds: string[], now: number): LaunchAccountsState {
  const [m1, m2] = machines;
  const made = (id: string, name: string, extra: Partial<LaunchAccount>, ago: number): LaunchAccount => ({
    id,
    name,
    agent: 'claude-code',
    kind: 'subscription',
    settings: {},
    folders: {},
    secret: null,
    accountKey: null,
    createdBy: 'you',
    createdAt: now - ago,
    ...extra,
  });
  const folderOn = (dir: string, id: string): Record<string, string> => {
    const out: Record<string, string> = {};
    if (m1) out[m1.id] = join(m1, '.orgus', dir, id);
    if (m2) out[m2.id] = join(m2, '.orgus', dir, id);
    return out;
  };
  const maxFolders = folderOn('claude-accounts', 'la_max');
  const chatgptFolders = folderOn('codex-accounts', 'la_chatgpt');
  const googleFolders = m1 ? { [m1.id]: join(m1, '.orgus', 'gemini-accounts', 'la_google') } : {};
  const builtIn: Record<string, string | null> = {
    'claude-code': 'anthropic:3f6c2a10-4b7e-4d21-9c55-0a1b2c3d4e5f',
    codex: 'chatgpt:5d2e8a71',
    'gemini-cli': null,
  };
  const accounts: LaunchAccount[] = [
    ...LAUNCH_ACCOUNT_AGENTS.map((agent) => made(defaultLaunchAccountId(agent), DEFAULT_LAUNCH_ACCOUNT_NAME, { agent, builtIn: true, accountKey: builtIn[agent] ?? null, createdBy: 'system', createdAt: 0 }, 0)),
    made('la_max', 'Claude Max', { folders: maxFolders, accountKey: 'anthropic:8e1d7c42-90ab-4cde-8f01-23456789abcd' }, 30 * 24 * HOUR),
    made('la_key', 'Team API key', { kind: 'api-key', secret: { fingerprint: '7c3e91ab', setAt: now - 9 * 24 * HOUR, setBy: 'you' }, accountKey: 'api-key:7c3e91ab' }, 9 * 24 * HOUR),
    made('la_azure', 'Azure Foundry', { kind: 'foundry', settings: { resource: 'contoso-ai' }, secret: { fingerprint: 'b04f22d9', setAt: now - 4 * 24 * HOUR, setBy: 'you' }, accountKey: 'foundry:contoso-ai' }, 4 * 24 * HOUR),
    made('la_aws', 'Bedrock us-east-1', { kind: 'bedrock', settings: { region: 'us-east-1', profile: 'claude' }, accountKey: 'bedrock:claude@us-east-1' }, 2 * 24 * HOUR),
    made('la_gcp', 'Vertex research', { kind: 'vertex', settings: { projectId: 'acme-research', region: 'global' }, accountKey: 'vertex:acme-research' }, 26 * HOUR),
    made('la_chatgpt', 'ChatGPT Pro', { agent: 'codex', folders: chatgptFolders, accountKey: 'chatgpt:a41c09e2' }, 12 * 24 * HOUR),
    made('la_openai', 'OpenAI platform key', { agent: 'codex', kind: 'api-key', secret: { fingerprint: '2f9a6c14', setAt: now - 6 * 24 * HOUR, setBy: 'you' }, accountKey: 'openai:2f9a6c14' }, 6 * 24 * HOUR),
    made('la_google', 'Google AI Pro', { agent: 'gemini-cli', folders: googleFolders, accountKey: null }, 3 * 24 * HOUR),
    made('la_gemini', 'Gemini API key', { agent: 'gemini-cli', kind: 'api-key', secret: { fingerprint: 'c81d3e57', setAt: now - 5 * 24 * HOUR, setBy: 'you' }, accountKey: 'gemini:c81d3e57' }, 5 * 24 * HOUR),
  ];
  const folders: LaunchFolderStatus[] = [];
  for (const m of machines) {
    folders.push({ machineId: m.id, accountId: defaultLaunchAccountId('claude-code'), path: join(m, '.claude'), state: 'signed-in', account: { key: 'anthropic:3f6c2a10-4b7e-4d21-9c55-0a1b2c3d4e5f', label: 'Acme', billing: 'usage' }, checkedAt: now - 40_000 });
    folders.push({ machineId: m.id, accountId: defaultLaunchAccountId('codex'), path: join(m, '.codex'), state: 'signed-in', account: { key: 'chatgpt:5d2e8a71', label: 'ChatGPT Plus', billing: 'subscription' }, checkedAt: now - 40_000 });
    folders.push({ machineId: m.id, accountId: defaultLaunchAccountId('gemini-cli'), path: home(m), state: 'signed-out', account: null, checkedAt: now - 40_000 });
  }
  if (m1) folders.push({ machineId: m1.id, accountId: 'la_max', path: maxFolders[m1.id]!, state: 'signed-in', account: { key: 'anthropic:8e1d7c42-90ab-4cde-8f01-23456789abcd', label: 'Claude Max 20x', billing: 'subscription' }, checkedAt: now - 40_000 });
  if (m2) folders.push({ machineId: m2.id, accountId: 'la_max', path: maxFolders[m2.id]!, state: 'signed-out', account: null, checkedAt: now - 70_000 });
  for (const m of [m1, m2]) if (m) folders.push({ machineId: m.id, accountId: 'la_chatgpt', path: chatgptFolders[m.id]!, state: 'signed-in', account: { key: 'chatgpt:a41c09e2', label: 'ChatGPT Pro', billing: 'subscription' }, checkedAt: now - 40_000 });
  if (m1) folders.push({ machineId: m1.id, accountId: 'la_google', path: googleFolders[m1.id]!, state: 'signed-out', account: null, checkedAt: now - 40_000 });
  const defaults: Record<string, LaunchAccountDefaults> = {};
  if (projectIds[0]) defaults[projectIds[0]] = { 'claude-code': 'la_max', codex: 'la_chatgpt' };
  if (projectIds[1]) defaults[projectIds[1]] = { 'claude-code': 'la_azure', 'gemini-cli': 'la_gemini' };
  // The Claude Code pool (ADR 0088): Max first, then the machine's own sign-in, then the key, which is over its limit for a while,
  // then Vertex, whose Google sign-in lapsed. Bedrock and Foundry stay outside, to be dragged in.
  const pools: LaunchAccountsState['pools'] = { 'claude-code': { members: ['la_max', defaultLaunchAccountId('claude-code'), 'la_key', 'la_gcp'], strategy: 'in-order', whenUnset: true } };
  const limits: LaunchAccountsState['limits'] = [
    { accountId: 'la_key', machineId: null, cause: 'usage-limit', summary: "Anthropic's API rate limit was reached.", until: now + 2 * HOUR, at: now - HOUR },
    { accountId: 'la_gcp', machineId: null, cause: 'auth', summary: 'Its Google Cloud sign-in expired.', until: now + 20 * HOUR, at: now - 4 * HOUR },
  ];
  return { accounts, defaults, folders, pools, limits, launchedAt: { la_max: now - 3 * HOUR, 'la_key': now - HOUR } };
}
